import { AlertTriangle, MoreHorizontal, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listTaxForms,
  type TaxForm,
  TAX_FORM_CATEGORY_OPTIONS,
  updateTaxForm,
} from "@/features/reference/api";
import { ApiError } from "@/lib/api";
import { ReferencePanel } from "./reference-panel";
import { TaxFormDialog } from "./tax-form-dialog";

const CATEGORY_LABELS = Object.fromEntries(
  TAX_FORM_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

export function TaxFormsPage() {
  const [items, setItems] = useState<TaxForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [appliesTo, setAppliesTo] = useState("all");
  const [active, setActive] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaxForm | undefined>();

  const refetch = useCallback(() => {
    setLoading(true);
    listTaxForms({
      search: search || undefined,
      category: category === "all" ? undefined : category,
      appliesTo: appliesTo === "all" ? undefined : appliesTo,
      active: active === "all" ? undefined : active,
      pageSize: 200,
    })
      .then((r) => {
        setItems(r.items);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [search, category, appliesTo, active]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function toggleActive(f: TaxForm) {
    try {
      await updateTaxForm(f.id, { isActive: !f.isActive });
      refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the form.");
    }
  }

  return (
    <ReferencePanel
      title="Tax-Form Registry"
      description="IRS and state forms the practice fills, their categories and applicability."
      loading={loading && items.length === 0 && !error}
      error={error}
      action={
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> Add form
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search form number or title..."
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger size="sm" className="w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {TAX_FORM_CATEGORY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={appliesTo} onValueChange={setAppliesTo}>
          <SelectTrigger size="sm" className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anyone</SelectItem>
            <SelectItem value="individual">Individuals</SelectItem>
            <SelectItem value="business">Businesses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger size="sm" className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No forms match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Drift</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((f) => {
                const drift = !!f.irsTitle && f.irsTitle !== f.title;
                return (
                  <TableRow key={f.id}>
                    <TableCell className="font-mono text-xs font-medium">{f.formNumber}</TableCell>
                    <TableCell className="font-medium">{f.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {CATEGORY_LABELS[f.category]}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {[f.appliesToIndividuals && "Individuals", f.appliesToBusinesses && "Businesses"]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      {drift ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="size-3" /> Drift
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {f.isActive ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" aria-label="Form actions">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditing(f);
                                setDialogOpen(true);
                              }}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => toggleActive(f)}>
                              {f.isActive ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <TaxFormDialog
        key={editing?.id ?? "new"}
        taxForm={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />
    </ReferencePanel>
  );
}
